import { unitBuildHotkey, type UnitKind } from "@dailywars/core/engine";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import { Button } from "#/components/ui/button";
import { Infotip, InfotipContent, InfotipTrigger } from "#/components/ui/infotip";
import { ScrollArea } from "#/components/ui/scroll-area";
import { cn } from "#/lib/utils";

import { gameDockedPanelFooterClassName } from "./GameDockedPanelShell";
import type { BuildOption, BuildOptionDisabledReason } from "./productionBuildMenuTypes";

const MAX_VISIBLE_BUILD_ROWS = 4;
const BUILD_LIST_SCROLL_PEEK_ROWS = 4.5;
const BUILD_ROW_HEIGHT_REM = 2.625;
const BUILD_ROW_GAP_REM = 0.375;
const BUILD_LIST_SCROLL_HEIGHT = `calc(${BUILD_LIST_SCROLL_PEEK_ROWS} * ${BUILD_ROW_HEIGHT_REM}rem + ${Math.ceil(BUILD_LIST_SCROLL_PEEK_ROWS) - 1} * ${BUILD_ROW_GAP_REM}rem)`;

export const BUILD_OPTION_DISABLED_REASON_LABEL: Record<BuildOptionDisabledReason, string> = {
  insufficient_funds: "Insufficient funds",
  no_airports: "No airports on today's map",
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

export function useProductionBuildMenuHotkeys({
  options,
  onChoose,
  enabled,
}: {
  options: BuildOption[];
  onChoose: (kind: UnitKind) => void;
  enabled: boolean;
}) {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableTarget(event.target)) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : "";
      if (!key) return;
      const option = options.find((entry) => unitBuildHotkey(entry.kind)?.toLowerCase() === key);
      if (!option || option.disabled) return;
      event.preventDefault();
      onChoose(option.kind);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, options, onChoose]);
}

function BuildOptionBuyButton({
  option,
  unitName,
  onChoose,
  className,
}: {
  option: BuildOption;
  unitName: string;
  onChoose: (kind: UnitKind) => void;
  className?: string;
}) {
  const buyButton = (
    <Button
      size="default"
      disabled={option.disabled}
      onClick={() => onChoose(option.kind)}
      aria-label={`Buy ${unitName}`}
      className={cn("w-full", className)}
    >
      Buy
    </Button>
  );

  if (option.disabledReason === null) return buyButton;

  const disabledLabel = BUILD_OPTION_DISABLED_REASON_LABEL[option.disabledReason];

  return (
    <div className={cn("flex min-w-0", className)}>
      <Infotip>
        <InfotipTrigger
          aria-label={disabledLabel}
          render={<span className="flex w-full min-w-0" />}
        >
          {buyButton}
        </InfotipTrigger>
        <InfotipContent side="top">{disabledLabel}</InfotipContent>
      </Infotip>
    </div>
  );
}

const SCROLL_EDGE_THRESHOLD_PX = 2;

function useScrollEdgeOverflow({
  viewportRef,
  contentRef,
  rowCount,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  rowCount: number;
}) {
  const [edges, setEdges] = useState({ canScrollUp: false, canScrollDown: false });

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      setEdges({
        canScrollUp: scrollTop > SCROLL_EDGE_THRESHOLD_PX,
        canScrollDown: scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_THRESHOLD_PX,
      });
    };

    update();
    viewport.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    if (content) observer.observe(content);
    return () => {
      viewport.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [contentRef, rowCount, viewportRef]);

  return edges;
}

function ScrollEdgeChevrons({
  canScrollUp,
  canScrollDown,
}: {
  canScrollUp: boolean;
  canScrollDown: boolean;
}) {
  return (
    <>
      {canScrollUp ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center bg-gradient-to-b from-popover via-popover/80 to-transparent pb-3 pt-0.5"
        >
          <ChevronUpIcon className="size-4 text-muted-foreground" />
        </div>
      ) : null}
      {canScrollDown ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center bg-gradient-to-t from-popover via-popover/80 to-transparent pb-0.5 pt-3"
        >
          <ChevronDownIcon className="size-4 text-muted-foreground" />
        </div>
      ) : null}
    </>
  );
}

export function BuildListScrollContainer({
  children,
  rowCount,
}: {
  children: ReactNode;
  rowCount: number;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown } = useScrollEdgeOverflow({
    viewportRef,
    contentRef,
    rowCount,
  });

  if (rowCount <= MAX_VISIBLE_BUILD_ROWS) {
    return <div className="space-y-1.5">{children}</div>;
  }

  return (
    <div className="relative w-full">
      <ScrollArea
        className="w-full"
        style={{ height: BUILD_LIST_SCROLL_HEIGHT }}
        viewportRef={viewportRef}
      >
        <div ref={contentRef} className="space-y-1.5 pr-3">
          {children}
        </div>
      </ScrollArea>
      <ScrollEdgeChevrons canScrollUp={canScrollUp} canScrollDown={canScrollDown} />
    </div>
  );
}

export function BuildMenuFooter({
  onCancel,
  onBack,
  buyOption,
  unitName,
  onChoose,
}: {
  onCancel: () => void;
  onBack?: () => void;
  buyOption?: BuildOption;
  unitName?: string;
  onChoose?: (kind: UnitKind) => void;
}) {
  const showBuy = buyOption && unitName && onChoose;

  return (
    <div className={cn(gameDockedPanelFooterClassName, showBuy && "flex-row")}>
      <Button
        onClick={onBack ?? onCancel}
        variant="outline"
        size="default"
        className={showBuy ? "flex-1" : "w-full sm:w-auto"}
      >
        Cancel
      </Button>
      {showBuy ? (
        <BuildOptionBuyButton
          option={buyOption}
          unitName={unitName}
          onChoose={onChoose}
          className="flex-1"
        />
      ) : null}
    </div>
  );
}
